/**
 * The subdomain cut-off — landing an old URL on its equivalent portal route.
 *
 * From the cut-off, `ofs.ketosource.co`, `meteoric`, `cvce`, `tokens`, `canvas`
 * and `build` are no longer doors. Cloudflare sends a top-level navigation on
 * any of them to `/app/legacy/<host-label>/<original path>` and this file works
 * out where that person was actually trying to go.
 *
 * **Why the mapping lives here and not in the Cloudflare rules.** A rule per
 * host per path would be a second copy of the registry at the edge — one that
 * goes stale the day a module adds a tab, and one nobody can test. The edge
 * rules carry only the host name; the path mapping is derived from the SAME
 * `/v1/me` payload the rail renders from, so the two cannot drift. It is also
 * what keeps the cut inside Cloudflare's Free-plan ceiling of ten single
 * redirect rules: six rules, one per host, and every deep link still lands.
 *
 * **Never the portal root.** A saved deep link that dumps someone at the front
 * door has lost their place, and they will not know which of eight areas had
 * the screen they had bookmarked. Every level of the fallback below lands
 * somewhere more specific than that, and an unrecognised host resolves to
 * `null` so the caller can say so rather than guess.
 */

/** The shape this resolver needs from `/v1/me` — a subset, not a second type. */
export interface LegacyArea {
  area_key: string;
  rail_order: number;
  modules: Array<{
    module_key: string;
    surface_url?: string | null;
    kind?: string | null;
    tabs: Array<{ tab_key: string; tab_order: number; surface_path?: string | null }>;
  }>;
}

/** The path prefix the edge rules redirect to. One constant, two consumers. */
export const LEGACY_PREFIX = "/app/legacy";

/** The host labels that stop being doors at the cut-off (KET-3492). */
export const LEGACY_HOSTS = ["ofs", "meteoric", "cvce", "tokens", "canvas", "build"] as const;

/** `ofs.ketosource.co` → `ofs`; a bare label passes through unchanged. */
export function hostLabel(host: string): string {
  return (host.split(":")[0] ?? host).trim().toLowerCase().split(".")[0] ?? "";
}

/** Compare paths ignoring a trailing slash, so `/history/` matches `/history`. */
function normalisePath(path: string): string {
  const withoutQuery = path.split(/[?#]/)[0] ?? path;
  const trimmed = withoutQuery.replace(/\/+$/, "");
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

/**
 * The route for one legacy URL, or `null` if this host was never a portal door.
 *
 * Resolution narrows in three steps, each landing somewhere more useful than
 * the last, and never at the root:
 *
 * 1. **The tab whose `surface_path` matches** — a saved deep link lands on the
 *    exact screen it named. The LONGEST match wins, so `/units-sold/overview`
 *    beats a module that merely claims `/`.
 * 2. **The host's first tab**, when only one module lives on that host. A root
 *    URL on `ofs` is unambiguous: there is one thing there.
 * 3. **The area holding most of the host's modules** (ties broken by rail
 *    order, nearest the top of the rail winning). `meteoric` carries five
 *    modules across two areas, and a root URL cannot say which — but landing on
 *    the area that holds four of them shows the person all five in the rail.
 */
/**
 * Where one legacy URL lands, and what was left over.
 *
 * `route` is the portal route. `at` is the part of the original path the route
 * itself cannot express — the deep path INSIDE the docked surface. Steps 1 and
 * 2 below narrow to a module and a tab; neither can say *which artefact*, so
 * without `at` the tail was dropped and every deep link opened a front page.
 */
export interface LegacyTarget {
  route: string;
  /** The surface path to open inside the docked module, or `null` for its home page. */
  at: string | null;
  /** The route opens a DOCKED module, whose frame is where the link's query and fragment belong. */
  docked: boolean;
}

/**
 * The full target for one legacy URL — route plus the leftover surface path.
 *
 * `resolveLegacyRoute` is this function's `route` half, kept as the name every
 * existing caller and test uses.
 */
export function resolveLegacyTarget(
  areas: LegacyArea[],
  host: string,
  path: string,
): LegacyTarget | null {
  const label = hostLabel(host);
  const wanted = normalisePath(path);

  interface Candidate {
    area: LegacyArea;
    moduleKey: string;
    docked: boolean;
    tabKey: string;
    tabOrder: number;
    surfacePath: string;
  }

  const onHost: Candidate[] = [];
  for (const area of areas) {
    for (const module of area.modules) {
      if (hostLabel(new URL(module.surface_url ?? "https://none.invalid").hostname) !== label) {
        continue;
      }
      for (const tab of module.tabs) {
        onHost.push({
          area,
          moduleKey: module.module_key,
          docked: module.kind === "docked",
          tabKey: tab.tab_key,
          tabOrder: tab.tab_order,
          surfacePath: normalisePath(tab.surface_path ?? "/"),
        });
      }
    }
  }

  if (onHost.length === 0) return null;

  // 1 — the deepest tab this path actually names.
  const matches = onHost
    .filter((c) => c.surfacePath !== "" && (wanted === c.surfacePath || wanted.startsWith(`${c.surfacePath}/`)))
    .sort((a, b) => b.surfacePath.length - a.surfacePath.length);
  if (matches[0]) {
    const hit = matches[0];
    return {
      route: `/app/${hit.area.area_key}/${hit.moduleKey}/${hit.tabKey}`,
      // Everything past the tab's own path. `/units-sold/overview/2026-08` on a
      // tab claiming `/units-sold/overview` leaves `/2026-08` — the part no
      // route can carry.
      at: deepPath(path, wanted, hit.surfacePath),
      docked: hit.docked,
    };
  }

  // 2 — one module on the host: its first tab is the only thing this URL meant.
  const modules = new Set(onHost.map((c) => c.moduleKey));
  if (modules.size === 1) {
    const first = [...onHost].sort((a, b) => a.tabOrder - b.tabOrder)[0]!;
    return {
      route: `/app/${first.area.area_key}/${first.moduleKey}/${first.tabKey}`,
      // The whole path, because the module claims no path of its own. This is
      // the Canvas case: one module, one tab, and `/infographics/<slug>/` is
      // the only thing that says which artefact was shared.
      at: deepPath(path, wanted, ""),
      docked: first.docked,
    };
  }

  // 3 — the area holding most of this host's modules, ties to the higher rail.
  const perArea = new Map<string, { area: LegacyArea; modules: Set<string> }>();
  for (const c of onHost) {
    const entry = perArea.get(c.area.area_key) ?? { area: c.area, modules: new Set<string>() };
    entry.modules.add(c.moduleKey);
    perArea.set(c.area.area_key, entry);
  }
  const [winner] = [...perArea.values()].sort(
    (a, b) => b.modules.size - a.modules.size || a.area.rail_order - b.area.rail_order,
  );
  // No `at` here on purpose: this step could not decide which MODULE the person
  // meant, so it has no surface to open a path inside. Handing the path on
  // would deep-link into whichever module the rail happens to open first.
  return winner ? { route: `/app/${winner.area.area_key}`, at: null, docked: false } : null;
}

/**
 * The original path minus the part the route already expresses.
 *
 * Takes the RAW path, not the normalised one, so a trailing slash survives —
 * `/infographics/<slug>/` and `/infographics/<slug>` are different requests to
 * a static host, and the first is the one Canvas publishes. Returns `null` when
 * nothing is left over, so a bare module URL still opens the module home page.
 */
function deepPath(raw: string, wanted: string, claimed: string): string | null {
  if (wanted === claimed) return null;
  // The query is added by `legacyDestination`, which knows whether the route
  // opens a surface to hand it to. Including it here would put it twice.
  const pathOnly = raw.split(/[?#]/)[0] ?? raw;
  const rest = pathOnly.slice(claimed.length);
  const withPath = rest.startsWith("/") ? rest : `/${rest}`;
  return withPath === "/" ? null : withPath;
}

/**
 * The portal URL one legacy link navigates to: the route, plus the original
 * query and the leftover surface path.
 *
 * When the link names a path inside a DOCKED surface, its query and fragment
 * belonged to that surface, not to the portal, so they travel inside `at`,
 * which `resolveDock` resolves against the surface and hands to the frame.
 * Left on the portal route they never reached it: a canvas screen link
 * (`/infographics/<slug>/?screen=Q2`) opened at the top of the canvas instead
 * of on screen Q2 (bug 3fc0402f).
 *
 * Everything else keeps the query on the route, as before: a native module
 * reads its own filters from there, and a route with no `at` has no surface
 * path to attach them to.
 */
export function legacyDestination(
  target: LegacyTarget,
  searchStr: string | undefined,
  hash?: string | undefined,
): string {
  const query = (searchStr ?? "").replace(/^\?/, "");
  const fragment = (hash ?? "").replace(/^#/, "");
  const search = new URLSearchParams();
  if (target.docked && target.at) {
    search.set("at", `${target.at}${query ? `?${query}` : ""}${fragment ? `#${fragment}` : ""}`);
  } else {
    new URLSearchParams(query).forEach((value, key) => search.append(key, value));
    if (target.at) search.set("at", target.at);
  }
  const qs = search.toString();
  return `${target.route}${qs ? `?${qs}` : ""}`;
}

export function resolveLegacyRoute(
  areas: LegacyArea[],
  host: string,
  path: string,
): string | null {
  return resolveLegacyTarget(areas, host, path)?.route ?? null;
}

/**
 * Split `/app/legacy/<host>/<rest>` back into the host and the original path.
 *
 * Returns `null` for anything that is not a legacy landing, so the route can
 * refuse rather than invent a host from a malformed URL.
 */
export function parseLegacyLanding(pathname: string): { host: string; path: string } | null {
  if (!pathname.startsWith(`${LEGACY_PREFIX}/`)) return null;
  const rest = pathname.slice(LEGACY_PREFIX.length + 1);
  const slash = rest.indexOf("/");
  const host = slash === -1 ? rest : rest.slice(0, slash);
  if (!host) return null;
  return { host, path: slash === -1 ? "/" : rest.slice(slash) };
}
